// CSR-CAN-10 (the principal's 18 September ask; GOALS/GOALS-SEARCH.html):
// Cleo can open a Canvas — any member of her World — as a tab in the deck of
// the session this conversation belongs to. The harness asks by appending one
// activity on the calling thread (the receipt); the Desk showing that session
// opens the tab on the deck it actually renders and answers on the loopback
// delivery route with what it opened. Opening is looking: it selects nothing,
// injects nothing, and reads no content into the conversation.
import * as Schema from "effect/Schema";
import { IsoDateTime } from "./baseSchemas.ts";

export const MEMBER_BOARD_OPEN_ACTIVITY_KIND = "member-board.open.requested";
export const MEMBER_BOARD_OPENED_SCHEMA_VERSION = "member-board-opened-v1";

export const CanvasHostEffect = Schema.Union([
  Schema.Struct({
    operation: Schema.Literals(["host.navigate", "host.focus"]),
    nodeId: Schema.String,
  }),
  Schema.Struct({
    operation: Schema.Literal("host.media"),
    nodeId: Schema.String,
    command: Schema.Literals(["play", "pause", "seek"]),
    seconds: Schema.optionalKey(Schema.Number),
  }),
  Schema.Struct({
    operation: Schema.Literal("host.dialog"),
    title: Schema.String,
    text: Schema.String,
  }),
]);
export const CanvasViewInteraction = Schema.Struct({
  sequence: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  viewerActorId: Schema.String,
  patch: Schema.Record(Schema.String, Schema.Unknown),
  host: Schema.optionalKey(CanvasHostEffect),
});
export type CanvasViewInteraction = typeof CanvasViewInteraction.Type;

export const MemberBoardOpenRequestPayload = Schema.Struct({
  requestId: Schema.String,
  worldKey: Schema.NullOr(Schema.String),
  /** The World session whose deck must show the tab; null for a Local thread. */
  sessionId: Schema.NullOr(Schema.String),
  memberId: Schema.String,
  label: Schema.NullOr(Schema.String),
  requestedAt: IsoDateTime,
  interactionExpiresAt: Schema.optionalKey(Schema.Number),
  interaction: Schema.optionalKey(CanvasViewInteraction),
});
export type MemberBoardOpenRequestPayload = typeof MemberBoardOpenRequestPayload.Type;

export const MemberBoardOpenedRevision = Schema.Struct({
  canvasId: Schema.String,
  headSequence: Schema.Number,
  revisionId: Schema.String,
});
export type MemberBoardOpenedRevision = typeof MemberBoardOpenedRevision.Type;

export const MemberBoardOpened = Schema.Struct({
  schemaVersion: Schema.Literal(MEMBER_BOARD_OPENED_SCHEMA_VERSION),
  requestId: Schema.String,
  worldKey: Schema.NullOr(Schema.String),
  sessionId: Schema.NullOr(Schema.String),
  memberId: Schema.String,
  /** The deck surface id the Desk fronted: `member:<memberId>`. */
  board: Schema.String,
  label: Schema.String,
  /** The head the Desk's own reader holds for a Canvas member, when it has read it. */
  revision: Schema.NullOr(MemberBoardOpenedRevision),
  openedAt: IsoDateTime,
  interaction: Schema.optionalKey(CanvasViewInteraction),
  /** Honest limits of this answer (the revision not yet read, and so on). */
  notes: Schema.Array(Schema.String),
});
export type MemberBoardOpened = typeof MemberBoardOpened.Type;

export const MemberBoardOpenDelivery = Schema.Struct({
  requestId: Schema.String,
  opened: MemberBoardOpened,
});
export type MemberBoardOpenDelivery = typeof MemberBoardOpenDelivery.Type;
